import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const NAV = [
  { path: '/',             icon: GridIcon,    label: 'Dashboard' },
  { path: '/orders',       icon: BagIcon,     label: 'Orders' },
  { path: '/reservations', icon: CalIcon,     label: 'Reservations' },
  { path: '/menu',         icon: MenuIcon,    label: 'Menu' },
  { path: '/customers',    icon: PeopleIcon,  label: 'Customers' },
  { path: '/analytics',    icon: ChartIcon,   label: 'Analytics' },
  { path: '/reviews',      icon: () => <span>⭐</span>, label: 'Reviews' },
  { path: '/campaigns',    icon: () => <span>📧</span>, label: 'Campaigns' },
  { path: '/settlements',  icon: () => <span>💰</span>, label: 'Settlements' },
  { path: '/payouts',      icon: () => <span>💳</span>, label: 'Payouts' },
  { path: '/loyalty',      icon: () => <span>⭐</span>, label: 'Loyalty' },
  { path: '/team',        icon: PeopleIcon,  label: 'Team' },
  { path: '/settings',     icon: GearIcon,    label: 'Settings' },
];

export default function Layout({ children }) {
  const { restaurant, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = restaurant?.role === 'reviews' ? NAV.filter(n => n.path === '/reviews') : NAV;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div style={styles.root}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, transform: mobileOpen ? 'translateX(0)' : undefined }}>
        {/* Logo */}
        <div style={styles.sidebarTop}>
          <div style={styles.logo}>
            <svg width="32" height="32" viewBox="0 0 120 120">
              <path d="M20 30 C20 20,30 10,50 10 L70 10 C90 10,100 20,100 30 L100 60 C100 70,90 80,70 80 L40 80 L20 95 L20 80 C20 80,20 70,20 60 Z" fill="white" opacity="0.9"/>
              <rect x="40" y="35" width="40" height="4" fill="#1A1208" rx="2"/>
              <rect x="42" y="39" width="4" height="25" fill="#1A1208" rx="2"/>
              <rect x="74" y="39" width="4" height="25" fill="#1A1208" rx="2"/>
              <rect x="38" y="64" width="44" height="6" fill="#1A1208" rx="3"/>
              <circle cx="85" cy="25" r="8" fill="#0A6847"/>
              <path d="M85 20 L85 30 M80 25 L90 25" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div>
              <div style={styles.logoName}>Table<span style={{ color: '#F97C35' }}>Chat</span></div>
              <div style={styles.logoSub}>Restaurant Portal</div>
            </div>
          </div>

          {/* Restaurant name pill */}
          {restaurant && (
            <div style={styles.restaurantPill}>
              <span style={{ fontSize: '1.1rem' }}>{restaurant.image_emoji || '🍽️'}</span>
              <div>
                <div style={styles.restaurantName}>{restaurant.name}</div>
                <div style={styles.restaurantStatus}>
                  <span style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                    background: restaurant.active ? '#4ade80' : '#f87171',
                    marginRight: 5
                  }}/>
                  {restaurant.active ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Nav links */}
        <nav style={styles.nav}>
          {navItems.map(({ path, icon: Icon, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              onClick={() => setMobileOpen(false)}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : {})
              })}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Sign out */}
        <div style={styles.sidebarBottom}>
          <button onClick={handleSignOut} style={styles.signOutBtn}>
            <LogOutIcon size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={styles.overlay}
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div style={styles.main}>
        {/* Mobile header */}
        <div style={styles.mobileHeader}>
          <button onClick={() => setMobileOpen(true)} style={styles.menuBtn}>
            <HamburgerIcon size={20} />
          </button>
          <span style={styles.mobileTitle}>{restaurant?.name}</span>
          <span style={{ width: 36 }} />
        </div>

        {/* Page content */}
        <div style={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { display: 'flex', minHeight: '100vh', background: 'var(--cream)' },
  sidebar: {
    width: 'var(--sidebar-w)',
    flexShrink: 0,
    background: 'var(--sidebar)',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0, left: 0, bottom: 0,
    zIndex: 50,
    overflowY: 'auto',
  },
  sidebarTop: { padding: '1.5rem 1rem 1rem' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.5rem' },
  logoName: { fontFamily: 'Fraunces, serif', fontSize: '1.1rem', fontWeight: 900, color: 'white' },
  logoSub: { fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', marginTop: 1 },
  restaurantPill: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '0.75rem',
  },
  restaurantName: { fontSize: '0.85rem', fontWeight: 700, color: 'white', lineHeight: 1.2 },
  restaurantStatus: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 },
  nav: { flex: 1, padding: '1rem 0.75rem', display: 'flex', flexDirection: 'column', gap: 2 },
  navLink: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '0.65rem 0.9rem', borderRadius: 8,
    color: 'rgba(255,255,255,0.55)',
    textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500,
    transition: 'all 0.15s',
  },
  navLinkActive: {
    background: 'rgba(232,93,4,0.2)',
    color: '#F97C35',
    fontWeight: 700,
  },
  sidebarBottom: { padding: '1rem 0.75rem 1.5rem' },
  signOutBtn: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '0.65rem 0.9rem', borderRadius: 8,
    background: 'transparent', border: 'none',
    color: 'rgba(255,255,255,0.35)', cursor: 'pointer',
    fontSize: '0.875rem', fontWeight: 500, width: '100%',
    transition: 'color 0.15s',
  },
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 40,
    display: 'none',
  },
  main: {
    flex: 1,
    marginLeft: 'var(--sidebar-w)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  mobileHeader: {
    display: 'none',
    alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    background: 'white',
    borderBottom: '1px solid var(--border)',
    position: 'sticky', top: 0, zIndex: 30,
  },
  mobileTitle: { fontFamily: 'Fraunces, serif', fontWeight: 700, fontSize: '1rem' },
  menuBtn: { background: 'none', border: 'none', color: 'var(--ink)', padding: 4 },
  content: { flex: 1, padding: '2rem', maxWidth: 1100, width: '100%', margin: '0 auto' },
};

// ── ICONS ────────────────────────────────────────────────────────────────────
function Icon({ size = 20, children }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
}
function GridIcon({ size })    { return <Icon size={size}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></Icon>; }
function BagIcon({ size })     { return <Icon size={size}><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></Icon>; }
function CalIcon({ size })     { return <Icon size={size}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Icon>; }
function MenuIcon({ size })    { return <Icon size={size}><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></Icon>; }
function PeopleIcon({ size })  { return <Icon size={size}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></Icon>; }
function GearIcon({ size })    { return <Icon size={size}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></Icon>; }
function ChartIcon({ size })   { return <Icon size={size}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></Icon>; }
function LogOutIcon({ size })  { return <Icon size={size}><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></Icon>; }
function HamburgerIcon({ size }) { return <Icon size={size}><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></Icon>; }
